import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import { APP_CONSTANTS } from 'src/consumer/constants'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import {
  buildBreadcrumbs,
  buildItemListJsonLd,
} from 'src/consumer/helpers/seoHelpers'
import { getFeatureList } from 'src/consumer/services/featureServices'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'

import ContactTeamModal from '../AumhumSite/ContactTeamModal'
import FloatingContactButtons from '../AumhumSite/FloatingContactButtons'
import { Footer, TopBar } from '../AumhumSite/Shell'
import { useContactNumber } from '../AumhumSite/useContactNumber'
import '../AumhumSite/AumhumSite.css'
import { getListItemsFromAssessmentList } from '../ListingPageComponentsAndHelpers/ListingPageHelpers'
import { LoadingSpinner } from '../Loaders'
import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'

/*
 * AssessmentListingPage
 *
 * Public listing of AUMHUM's clinician-validated self-assessments.
 * Rewritten to match the marketing site + condition-page design
 * language (aumhum-site-root tokens, TopBar, Footer, floating
 * contact buttons, Connect-with-our-team modal). The visual pattern
 * mirrors the condition grid on the home page so the two feel
 * continuous.
 *
 * Data shape comes from getFeatureList('assessment') via the
 * existing `getListItemsFromAssessmentList` helper, so we don't need
 * any new endpoints.
 */

export default function AssessmentListingPage({ embedded = false, linkBase = '/assessment' }) {
  const [pageLoading, setPageLoading] = useState(false)
  // `loadingMore` is distinct from pageLoading so we can tell
  // useInfiniteScroll to stop firing during a pagination fetch —
  // previously the hook saw loading=false during loadNextPage and
  // kept refiring in prerender environments where the sentry is
  // always inside the viewport, causing a runaway loop.
  const [loadingMore, setLoadingMore] = useState(false)
  const [items, setItems] = useState([])
  const [pageNumber, setPageNumber] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [authOpen, setAuthOpen] = useState(false)
  const [contactModal, setContactModal] = useState({ open: false, mode: 'call' })
  const contact = useContactNumber()

  async function loadFirstPage() {
    setPageLoading(true)
    try {
      const resp = await getFeatureList('assessment')
      if (resp?.status === 200) {
        const list = getListItemsFromAssessmentList(resp.data || [])
        setItems(list)
        if (!list.length) setHasNextPage(false)
      } else {
        setHasNextPage(false)
      }
    } catch {
      setHasNextPage(false)
    } finally {
      setPageLoading(false)
    }
  }

  async function loadNextPage() {
    if (loadingMore) return
    setLoadingMore(true)
    try {
      const nextPage = pageNumber + 1
      const resp = await getFeatureList('assessment', nextPage)
      if (resp?.status === 200 && resp?.data?.length) {
        const list = getListItemsFromAssessmentList(resp.data)
        setPageNumber(nextPage)
        setItems((prev) => [...prev, ...list])
      } else {
        setHasNextPage(false)
      }
    } catch {
      // Network/API hiccup: stop paginating rather than spinning.
      setHasNextPage(false)
    } finally {
      setLoadingMore(false)
    }
  }

  const [sentryRef] = useInfiniteScroll({
    // Block both initial and in-flight paginations so the hook can't
    // refire onLoadMore while a page fetch is still resolving.
    loading: pageLoading || loadingMore,
    hasNextPage,
    onLoadMore: loadNextPage,
    // 20px — original pre-rewrite margin. A big margin (e.g. 400px)
    // made the sentry intersect immediately in headless viewports
    // and triggered a runaway fetch loop in the prerender pipeline.
    rootMargin: '0px 0px 20px 0px',
  })

  useEffect(() => {
    loadFirstPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Memoise the JSON-LD payload so Helmet's child list identity is
  // stable across renders — we were previously rebuilding the array
  // (and the BreadcrumbList / ItemList objects inside) on every
  // render, which made react-helmet-async thrash its registry and
  // gave root-App.jsx's default-title Helmet a chance to win the race.
  const seoJsonLd = useMemo(
    () => [
      buildBreadcrumbs([
        { name: 'Home', path: '/' },
        { name: 'Self-assessments', path: '/assessment' },
      ]),
      buildItemListJsonLd({
        name: 'AUMHUM mental health self-assessments',
        description:
          'Validated self-report screeners across depression, anxiety, OCD, bipolar, substance use, autism-spectrum, and personality.',
        pathname: '/assessment',
        items: items.map((it) => ({ name: it.title, path: it.link })),
      }),
    ],
    [items],
  )

  return (
    <>
      <SEOComponent
        title="Mental health self-assessments — clinician-validated | AUMHUM"
        description="Take short, confidential, clinician-validated mental health screeners — PHQ-9, GAD-7, Y-BOCS and more. Understand what’s going on and take the next step at your own pace."
        image={APP_CONSTANTS.APP_LOGO_LINK}
        pathname="/assessment"
        keywordsArr={[
          'mental health assessment',
          'self-assessment',
          'PHQ-9',
          'GAD-7',
          'depression test',
          'anxiety test',
          'OCD test',
          'clinical screener',
          'psychological assessment',
        ]}
        jsonLd={seoJsonLd}
      />

      <ClubbedAuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        loginVariant
      />

      <div className="aumhum-site-root">
        {!embedded && (
          <TopBar
            onLogin={() => setAuthOpen(true)}
            contact={contact}
            onContactClick={() => setContactModal({ open: true, mode: 'call' })}
          />
        )}

        <main id="main" className="as-page">
          {/* ── Hero ──────────────────────────────────── */}
          <section className="as-assess-hero">
            <div className="as-container">
              <div className="as-assess-hero-grid">
                <div>
                  <div className="as-eyebrow">Self-assessment</div>
                  <h1 className="as-h1">
                    Start with a <span className="as-brand-gradient">clear-eyed</span>
                    {' '}check-in
                  </h1>
                  <p className="as-lede">
                    Short, private, clinician-validated questionnaires — the same screeners
                    used in mental health practice. They don’t diagnose, but they help
                    you and the care team see the shape of what’s going on.
                  </p>
                  <ul className="as-assess-hero-points">
                    <li>
                      <Dot /> Takes 3–10 minutes each
                    </li>
                    <li>
                      <Dot /> Private — your result is only visible to you and the care
                      team
                    </li>
                    <li>
                      <Dot /> You’ll be asked to sign in so it stays with you
                    </li>
                  </ul>
                </div>
                <aside className="as-assess-hero-card" aria-hidden="true">
                  <div className="as-assess-hero-card-glyph">
                    <ClipboardIcon />
                  </div>
                  <div className="as-assess-hero-card-title">
                    Not a diagnostic tool
                  </div>
                  <p className="as-assess-hero-card-body">
                    Think of this as a starting point. Anything clinically significant is
                    reviewed by our psychiatry and psychology team before any conclusions
                    are drawn.
                  </p>
                </aside>
              </div>
            </div>
          </section>

          {/* ── Listing ───────────────────────────────── */}
          <section className="as-section">
            <div className="as-container">
              <div className="as-section-header as-max-w-3">
                <div className="as-eyebrow">Available screeners</div>
                <h2 className="as-h2">Pick the one that feels closest</h2>
                <p className="as-lede">
                  Not sure which to pick? The care team can guide you — or start with the
                  one that names what you’re noticing the loudest.
                </p>
              </div>

              {/* Render inline skeleton cards while the first page is
                  loading rather than a fullscreen LoadingSpinner overlay.
                  Keeps the hero + section header visible so the page
                  feels like it\u2019s filling in rather than waiting. */}
              {pageLoading && items.length === 0 ? (
                <ul className="as-assess-grid as-mt-10" role="list" aria-busy="true">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <li key={`sk-${i}`}>
                      <div className="as-assess-card as-assess-card-skeleton">
                        <div className="as-assess-card-media as-assess-card-media-skel" />
                        <div className="as-assess-card-body">
                          <div className="as-skel-bar as-skel-bar-title" />
                          <div className="as-skel-bar as-skel-bar-line" />
                          <div className="as-skel-bar as-skel-bar-line as-skel-bar-short" />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <>
                  <ul className="as-assess-grid as-mt-10" role="list">
                    {items.map((item) => (
                      <li key={item.slug || item.id}>
                        <Link
                          to={`${linkBase}/${item.slug || item.id}`}
                          className="as-assess-card"
                        >
                          <div className="as-assess-card-media">
                            <img
                              src={getImageLink(item.imageId)}
                              alt=""
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <div className="as-assess-card-body">
                            <div className="as-assess-card-title">{item.title}</div>
                            {item.description ? (
                              <p className="as-assess-card-desc">{item.description}</p>
                            ) : null}
                          </div>
                          <div className="as-assess-card-cta">
                            <span>Start test</span>
                            <Arrow />
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>

                  {hasNextPage ? (
                    <div
                      ref={sentryRef}
                      className="as-assess-sentry"
                      aria-live="polite"
                    >
                      <span className="as-assess-sentry-dot" />
                      <span className="as-assess-sentry-dot" />
                      <span className="as-assess-sentry-dot" />
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </section>

          {/* ── Care CTA ──────────────────────────────── */}
          <section className="as-section as-section-alt">
            <div className="as-container">
              <div className="as-assess-cta">
                <div>
                  <div className="as-eyebrow">When a screener isn’t enough</div>
                  <h2 className="as-h2">Talk to a real clinician</h2>
                  <p className="as-lede">
                    If something’s been going on for a while, or the result worries
                    you, a short call with our care team is the quickest path to the right
                    kind of help.
                  </p>
                </div>
                <div className="as-assess-cta-actions">
                  <button
                    type="button"
                    className="as-btn as-btn-primary"
                    onClick={() =>
                      setContactModal({ open: true, mode: 'call' })
                    }
                  >
                    Book an expert consultation
                  </button>
                  <button
                    type="button"
                    className="as-btn as-btn-secondary"
                    onClick={() =>
                      setContactModal({ open: true, mode: 'whatsapp' })
                    }
                  >
                    Or chat on WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </section>
        </main>

        {!embedded && <Footer />}

        {!embedded && (
          <FloatingContactButtons
            onPhoneClick={() => setContactModal({ open: true, mode: 'call' })}
            onWhatsappClick={() =>
              setContactModal({ open: true, mode: 'whatsapp' })
            }
          />
        )}
        <ContactTeamModal
          open={contactModal.open}
          mode={contactModal.mode}
          onClose={() => setContactModal((s) => ({ ...s, open: false }))}
          contact={contact}
        />
      </div>
    </>
  )
}

// ── icons (inline) ──────────────────────────────────────────

function Dot() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: '0.5rem',
        height: '0.5rem',
        borderRadius: '9999px',
        background: 'currentColor',
        opacity: 0.7,
        marginRight: '0.625rem',
        verticalAlign: 'middle',
      }}
    />
  )
}

function Arrow() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  )
}

function ClipboardIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1" />
      <line x1="9" y1="10" x2="15" y2="10" />
      <line x1="9" y1="14" x2="15" y2="14" />
      <line x1="9" y1="18" x2="13" y2="18" />
    </svg>
  )
}
